import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Info, X } from "lucide-react";
import { TONES } from "./tokens";

// ── Card ────────────────────────────────────────────────────────────────
export const Card = ({ className = "", children, style }) => (
  <div
    className={`bg-white border border-[#E6E9EF] rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}
    style={style}
  >
    {children}
  </div>
);

export const CardHeader = ({ icon, tone = "blue", title, desc, action, children }) => {
  const t = TONES[tone] || TONES.blue;
  return (
    <div className="flex items-center gap-3 px-[18px] py-[14px] border-b border-[#EEF1F5]">
      {icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: t.bg, color: t.fg }}
        >
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {title && <div className="text-sm font-semibold text-[#0F172A]">{title}</div>}
        {desc && <div className="text-[12px] text-[#64748B] mt-0.5">{desc}</div>}
        {children}
      </div>
      {action}
    </div>
  );
};

export const CardBody = ({ className = "", tight = false, children, style }) => (
  <div className={`${tight ? "px-[18px] py-3" : "px-[18px] py-4"} ${className}`} style={style}>
    {children}
  </div>
);

// ── Field ───────────────────────────────────────────────────────────────
export const Field = ({ label, required, help, children, style, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`} style={style}>
    {label && (
      <label className="text-[12px] font-semibold text-[#334155]">
        {label}
        {required && <span className="text-[#E11D48] ml-0.5">*</span>}
      </label>
    )}
    {children}
    {help && <div className="text-[11px] text-[#94A3B8] leading-snug">{help}</div>}
  </div>
);

// ── Affix ───────────────────────────────────────────────────────────────
export const Affix = ({ pre, post, children }) => (
  <div className="relative flex items-center">
    {pre && (
      <span className="absolute left-3 text-[12px] font-semibold text-[#64748B] pointer-events-none z-10">
        {pre}
      </span>
    )}
    {children}
    {post && (
      <span className="absolute right-3 text-[12px] font-semibold text-[#64748B] pointer-events-none">
        {post}
      </span>
    )}
  </div>
);

// ── Inputs ──────────────────────────────────────────────────────────────
export const Input = ({ className = "", style, mono, prePadding, disabled, ...rest }) => (
  <input
    {...rest}
    disabled={disabled}
    className={`h-9 px-3 text-[13px] text-[#0F172A] bg-white border border-[#D9DEE6] rounded-md outline-none w-full
      focus:border-primary focus:ring-2 focus:ring-primary/10 placeholder:text-[#94A3B8]
      ${mono ? "font-mono" : ""}
      ${disabled ? "bg-[#F1F4F8] cursor-not-allowed" : ""}
      ${className}`}
    style={{ paddingLeft: prePadding, ...style }}
  />
);

export const Select = ({ className = "", children, disabled, ...rest }) => (
  <select
    {...rest}
    disabled={disabled}
    className={`h-9 px-3 pr-8 text-[13px] text-[#0F172A] bg-white border border-[#D9DEE6] rounded-md outline-none w-full appearance-none
      focus:border-primary focus:ring-2 focus:ring-primary/10
      ${disabled ? "bg-[#F1F4F8] cursor-not-allowed" : ""}
      ${className}`}
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
      backgroundPosition: "right 10px center",
      backgroundRepeat: "no-repeat",
    }}
  >
    {children}
  </select>
);

// ── Switch ──────────────────────────────────────────────────────────────
export const Switch = ({ on, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!on)}
    disabled={disabled}
    className={`relative w-10 h-[22px] rounded-full transition-colors shrink-0 ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    }`}
    style={{ background: on ? "var(--primary)" : "#CBD5E1" }}
    aria-pressed={on}
  >
    <span
      className="absolute top-[2px] w-[18px] h-[18px] bg-white rounded-full shadow transition-all"
      style={{ left: on ? "20px" : "2px" }}
    />
  </button>
);

export const ToggleRow = ({ on, onChange, title, desc, right, disabled }) => (
  <div className="flex items-center gap-3 py-[13px] border-b border-[#EEF1F5] last:border-b-0">
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold text-[#0F172A]">{title}</div>
      {desc && <div className="text-[12px] text-[#64748B] mt-0.5 leading-snug">{desc}</div>}
    </div>
    {right ? right : <Switch on={on} onChange={onChange} disabled={disabled} />}
  </div>
);

// ── Segmented ───────────────────────────────────────────────────────────
export const Seg = ({ value, onChange, options, disabled, size = "md" }) => {
  const h = size === "sm" ? "h-7" : "h-9";
  const px = size === "sm" ? "px-2.5" : "px-3";
  return (
    <div
      className={`inline-flex p-0.5 bg-[#F1F4F8] border border-[#E6E9EF] rounded-md gap-0.5 ${h} ${disabled ? "opacity-60" : ""}`}
    >
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => !disabled && onChange(o.value)}
          disabled={disabled}
          className={`flex items-center gap-1.5 ${px} text-[12px] font-semibold rounded transition-colors ${
            value === o.value
              ? "bg-white text-[#0F172A] shadow-sm"
              : "text-[#64748B] hover:text-[#334155]"
          } ${disabled ? "cursor-not-allowed" : ""}`}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
};

// ── Badge / Tag ─────────────────────────────────────────────────────────
export const Badge = ({ tone = "slate", children }) => {
  const t = TONES[tone] || TONES.slate;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ background: t.bg, color: t.fg }}
    >
      <span className="w-[5px] h-[5px] rounded-full" style={{ background: t.fg }} />
      {children}
    </span>
  );
};

export const Tag = ({ tone, children, icon, className = "", style }) => {
  const t = tone ? TONES[tone] : { bg: "#F1F5F9", fg: "#475569" };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${className}`}
      style={{ background: t.bg, color: t.fg, ...style }}
    >
      {icon}
      {children}
    </span>
  );
};

// ── KPI ─────────────────────────────────────────────────────────────────
export const KPI = ({ icon, tone = "blue", label, value, delta, deltaTone }) => {
  const t = TONES[tone] || TONES.blue;
  const deltaColor =
    deltaTone === "pos" ? "#15803C" : deltaTone === "neg" ? "#B91C1C" : "#64748B";
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: t.bg, color: t.fg }}
        >
          {icon}
        </div>
      </div>
      <div className="text-[12px] font-medium text-[#64748B]">{label}</div>
      <div className="text-[26px] font-bold text-[#0F172A] tracking-tight mt-0.5 leading-none">
        {value}
      </div>
      {delta && (
        <div className="text-[11px] font-semibold mt-2" style={{ color: deltaColor }}>
          {delta}
        </div>
      )}
    </Card>
  );
};

// ── Callout ─────────────────────────────────────────────────────────────
export const Callout = ({ tone = "blue", icon, children, className = "" }) => {
  const t = TONES[tone] || TONES.blue;
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border ${className}`}
      style={{ background: t.bg, borderColor: t.fg + "33" }}
    >
      <span className="shrink-0 mt-0.5" style={{ color: t.fg }}>
        {icon || <Info size={16} />}
      </span>
      <div className="text-[12.5px] leading-relaxed flex-1" style={{ color: t.fg }}>
        {children}
      </div>
    </div>
  );
};

// ── KV ──────────────────────────────────────────────────────────────────
export const KV = ({ label, value, className = "" }) => (
  <div
    className={`flex justify-between items-center py-2 border-b border-[#F1F4F8] last:border-b-0 ${className}`}
  >
    <span className="text-[12px] text-[#64748B]">{label}</span>
    <span className="text-[12.5px] font-semibold text-[#0F172A]">{value}</span>
  </div>
);

// ── Button ──────────────────────────────────────────────────────────────
export const Btn = ({
  variant = "primary",
  size = "md",
  icon,
  children,
  className = "",
  disabled,
  ...rest
}) => {
  const base =
    "inline-flex items-center justify-center gap-1.5 font-semibold rounded-md transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes = {
    sm: "h-7 px-2.5 text-[12px]",
    md: "h-9 px-3.5 text-[13px]",
    lg: "h-10 px-4 text-[13px]",
  };
  const variants = {
    primary: "bg-primary text-white hover:bg-primary/90",
    ghost: "bg-transparent text-[#334155] border border-[#D9DEE6] hover:bg-[#F1F4F8]",
    soft: "bg-primary/10 text-primary hover:bg-primary/15",
    danger: "bg-[#E11D48] text-white hover:bg-[#BE123C]",
    link: "bg-transparent text-primary hover:underline px-0",
  };
  return (
    <button
      {...rest}
      disabled={disabled}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {icon}
      {children}
    </button>
  );
};

export const FormBar = ({ hint, onCancel, onSave, saveLabel = "Save changes", saving, disabled }) => (
  <div className="sticky bottom-0 -mx-6 mt-6 flex items-center gap-3 px-6 py-3 bg-white border-t border-[#E6E9EF] shadow-[0_-4px_12px_rgba(15,23,42,0.04)]">
    {hint && (
      <div className="flex items-center gap-1.5 text-[12px] text-[#64748B]">
        <Info size={14} />
        {hint}
      </div>
    )}
    <div className="flex-1" />
    {onCancel && (
      <Btn variant="ghost" onClick={onCancel}>
        Cancel
      </Btn>
    )}
    <Btn variant="primary" onClick={onSave} disabled={saving || disabled}>
      <Check size={15} />
      {saving ? "Saving…" : saveLabel}
    </Btn>
  </div>
);
